import { useCallback, useMemo, useState } from 'react'

import type { GiftItem } from '../../../../../core/requests/checkout'
import { useOrderForm } from '../../../../../core/requests/checkout'
import { sendMetric } from '../../../../../vtexLakeHouse'
import { MetricAction, MetricShowcase } from '../../../../../core/constants'
import { logger } from '../../../../../logger'

export const useGifts = () => {
  const { orderForm, updateSelectedGifts } = useOrderForm()
  const [itemInLoading, setItemInLoading] = useState<string>()

  const selectableGifts = useMemo(() => {
    return orderForm?.selectableGifts ?? []
  }, [orderForm])

  const getGiftGroupdByItemId = useCallback(
    (itemId: string) => {
      return selectableGifts.find(
        (selectableGift) =>
          !!selectableGift.availableGifts.find((gift) => gift.id === itemId)
      )
    },
    [selectableGifts]
  )

  const getGroupSelectedQuantityByItemId = useCallback(
    (itemId: string) => {
      const group = getGiftGroupdByItemId(itemId)

      return (
        group?.availableGifts?.filter((gift) => gift.isSelected)?.length ?? 0
      )
    },
    [getGiftGroupdByItemId]
  )

  const selectGift = useCallback(
    async (item: GiftItem) => {
      try {
        setItemInLoading(item.uniqueId ?? item.id)
        const group = getGiftGroupdByItemId(item.id)

        if (!group) return

        if (item.isSelected) {
          const filteredList = group.availableGifts.filter(
            (gift) => gift.isSelected && gift.id !== item.id
          )

          sendMetric({
            action: MetricAction.GiftItemRemove,
            showcase: MetricShowcase.Gifts,
            value: item.id,
          })

          await updateSelectedGifts(group.id, filteredList)

          return
        }

        const newList = group.availableGifts
          .filter((gift) => gift.isSelected)
          .concat(item)

        sendMetric({
          action: MetricAction.GiftItemSelect,
          showcase: MetricShowcase.Gifts,
          value: item.id,
        })

        await updateSelectedGifts(group.id, newList)
      } catch (error) {
        logger.error({
          workflowType: 'select-gifts-error',
          error: error instanceof Error ? error : new Error(String(error)),
        })
      } finally {
        setItemInLoading(undefined)
      }
    },
    [setItemInLoading, getGiftGroupdByItemId, updateSelectedGifts]
  )

  return {
    selectableGifts,
    itemInLoading,
    selectGift,
    getGroupSelectedQuantityByItemId,
  }
}
